import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { EventLinkInfo, RetrievedEventLinks } from "@/store/event-links/types";
import { EventListPaginatedSearchQueryParams, PaginatedSearchQueryParams } from "@/store/types";
import PaginatedSearch, { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import router from "@/router";

const EventLinksStore = namespace("eventLinks");

@Component({})
export default class EventLinksMixin extends Mixins(PaginatedSearch) {
	@Prop(Number) baseEventId: number;
	@EventLinksStore.Getter("getEventLinksList") private readonly storedEventLinksList: RetrievedEventLinks;
	@EventLinksStore.Getter("getEventLinksTable") private readonly storedEventLinksTable: RetrievedEventLinks;
	@EventLinksStore.Action("fetchEventLinksTable") private readonly fetchEventLinksTable: (request: PaginatedSearchRequest) => Promise<void>;
	@EventLinksStore.Action("fetchEventLinksList") private readonly fetchEventLinksList: (request: PaginatedSearchRequest) => Promise<void>;
	@EventLinksStore.Mutation("setEventLinksTable") private readonly setEventLinksTable: (eventLinks: RetrievedEventLinks) => void;

	protected pageNumber: number = 1;
	protected pageSize: number = 5;
	protected sortBy: string = "eventId";
	protected sortDesc: boolean = false;
	protected searchTerm: string = "";
	protected tableViewPageNumber: number = 1;

	protected isLoading: boolean = false;

	protected get retrievedEventLinksTable(): RetrievedEventLinks {
		return this.storedEventLinksTable || {
			eventLinks:  {
				data: [],
				pageNumber: 1,
				pageSize: 25,
				totalRecords: 0,
			},
			noPermissionCount: 0
		};
	}

	// Used to keep the first page of results in the list view.
	protected get retrievedEventLinksList(): RetrievedEventLinks {
		return this.storedEventLinksList || {
			eventLinks:  {
				data: [],
				pageNumber: 1,
				pageSize: 25,
				totalRecords: 0,
			},
			noPermissionCount: 0
		};
	}

	created(): void {
		this.tableViewPageNumber = 1;
		this.setEventLinksTable(this.retrievedEventLinksList);
	}

	@Watch("baseEventId")
	async retrieveFirstPage(): Promise<void> {
		await this.retrieveEventLinksList(false);
		this.setEventLinksTable(this.retrievedEventLinksList);
	}

	updateSearchFilter(newSearchFilter: PaginatedSearchQueryParams): void {
		this.tableViewPageNumber = newSearchFilter.page || 1;

		this.retrieveEventLinksTable(newSearchFilter, true);
	}

	async retrieveEventLinksList(live: boolean = false): Promise<void> {
		try {
			this.isLoading = true;

			const queryParams: EventListPaginatedSearchQueryParams = {
				page: this.pageNumber,
				pageSize: this.pageSize,
				sortBy: this.sortBy,
				sortDesc: this.sortDesc,
				searchTerm: this.searchTerm,
				eventId: this.baseEventId,
				live: live
			};

			const request = this.generateNewPaginatedSearchRequest(queryParams);

			await this.fetchEventLinksList(request);
		}
		catch (error) {
			console.error("Failed to retrieve event links: ", error);
		}
		finally {
			this.isLoading = false;
		}
	}

	async retrieveEventLinksTable(paginatedSearchQueryParams: PaginatedSearchQueryParams = null, live: boolean = false): Promise<void> {
		try {
			this.isLoading = true;

			const queryParams: EventListPaginatedSearchQueryParams = {
				page: paginatedSearchQueryParams?.page || this.tableViewPageNumber,
				pageSize: paginatedSearchQueryParams?.pageSize || this.pageSize,
				sortBy: paginatedSearchQueryParams?.sortBy || this.sortBy,
				sortDesc: paginatedSearchQueryParams?.sortDesc || this.sortDesc,
				searchTerm: paginatedSearchQueryParams?.searchTerm || this.searchTerm,
				eventId: this.baseEventId,
				live: live
			};

			const request = this.generateNewPaginatedSearchRequest(queryParams);

			await this.fetchEventLinksTable(request);
		}
		catch (error) {
			console.error("Failed to retrieve event links: ", error);
		}
		finally {
			this.isLoading = false;
		}
	}

	openEventReview(eventLink: EventLinkInfo): void {
		if (!eventLink.isEventActive) {
			return;
		}

		const path = `/eventsearch/${eventLink.eventId}`;
		const routeData = router.resolve({ path });
		window.open(routeData.href, path);
	}

	isEventLinkActive(eventLink: EventLinkInfo): boolean {
		return eventLink.isEventActive;
	}

	get noPermissionMessage(): string {
		var eventLinksCount = this.retrievedEventLinksList.eventLinks.data.length;
		if (eventLinksCount === 0 && this.retrievedEventLinksList.noPermissionCount === 1) {
			return `1 event you do not have permission to view.`;
		}

		if (eventLinksCount === 0 && this.retrievedEventLinksList.noPermissionCount > 1) {
			return `${this.retrievedEventLinksList.noPermissionCount} events you do not have permission to view.`;
		}

		if (eventLinksCount > 0 && this.retrievedEventLinksList.noPermissionCount === 1) {
			return `Plus 1 more event you do not have permission to view.`;
		}

		if (eventLinksCount > 0 && this.retrievedEventLinksList.noPermissionCount > 1) {
			return `Plus ${this.retrievedEventLinksList.noPermissionCount} more events you do not have permission to view.`;
		}

		return "";
	}

	get displayNoPermissionMessage(): boolean {
		return this.retrievedEventLinksList.noPermissionCount > 0;
	}
}
